import { useCallback, useMemo, useState } from 'react';
import {
  TOTAL_BP,
  destinationUrlSchema,
  distributeRemainder,
  percentToBp,
  sumBp,
  validateWeightTotal,
} from '@redirector/shared';

export type Destination = {
  id: number;
  url: string;
  weightBp: number;
  enabled: boolean;
  position: number;
};

export type DraftRow = {
  /** Stable across edits, including for rows that have no server id yet. */
  key: string;
  id: number | null;
  url: string;
  weightBp: number;
  enabled: boolean;
  locked: boolean;
  urlError?: string;
};

export type WeightDraft = {
  rows: readonly DraftRow[];
  totalBp: number;
  remainingBp: number;
  isValid: boolean;
  isDirty: boolean;
/** How many rows sit at 0%. Reported, not refused: a 0% row is parked. */
  unallocated: number;
  hasDisabled: boolean;
  effectivePct: (key: string) => number;
  setWeightPct: (key: string, percent: number) => void;
  toggleLock: (key: string) => void;
  toggleEnabled: (key: string) => void;
  setUrl: (key: string, url: string) => void;
  addRow: () => void;
  addMany: (urls: readonly string[]) => void;
  removeRow: (key: string) => void;
  distributeAll: () => void;
  reset: () => void;
  toPayload: () => { url: string; weightBp: number; enabled: boolean }[];
};

let sequence = 0;
const nextKey = (): string => `draft-${(sequence += 1)}`;

const fromServer = (destinations: readonly Destination[]): DraftRow[] =>
  destinations.map((destination) => ({
    key: `saved-${destination.id}`,
    id: destination.id,
    url: destination.url,
    weightBp: destination.weightBp,
    enabled: destination.enabled,
    locked: false,
  }));

const lockedIndicesOf = (rows: readonly DraftRow[]): number[] =>
  rows.map((row, index) => (row.locked ? index : -1)).filter((index) => index !== -1);

/** Validates URLs across the whole set, so duplicates can be attributed. */
function withUrlErrors(rows: readonly DraftRow[]): DraftRow[] {
  const seen = new Map<string, number>();
  for (const row of rows) {
    const url = row.url.trim();
    if (url !== '') seen.set(url, (seen.get(url) ?? 0) + 1);
  }

  return rows.map((row) => {
    const url = row.url.trim();
    if (url === '') return { ...row, urlError: 'A destination URL is required' };
    if ((seen.get(url) ?? 0) > 1) {
      return { ...row, urlError: 'Each destination may appear only once in an entry' };
    }
    const parsed = destinationUrlSchema.safeParse(url);
    if (!parsed.success) {
      return { ...row, urlError: parsed.error.issues[0]?.message ?? 'That is not a valid URL' };
    }
    const { urlError: _drop, ...rest } = row;
    return rest;
  });
}

export function useWeightDraft(initial: readonly Destination[]): WeightDraft {
  const baseline = useMemo(() => fromServer(initial), [initial]);
  const [rows, setRows] = useState<DraftRow[]>(baseline);

  const totalBp = sumBp(rows.map((row) => row.weightBp));
  const enabledTotalBp = sumBp(rows.filter((row) => row.enabled).map((row) => row.weightBp));

  /*
   * Sets one weight and touches nothing else.
   *
   * This used to rebalance every other row on every keystroke, to keep the
   * total at exactly 100%. It was the single worst thing about the editor:
   * typing the second of three weights moved the first, so there was no order
   * in which a split could be entered — the operator was fighting the
   * arithmetic instead of expressing an intention. Now the number typed is the
   * number stored, `remainingBp` says what is still unallocated, and
   * `distributeAll` is the one explicit action that closes the gap.
   */
  const setWeightPct = useCallback((key: string, percent: number) => {
    setRows((current) => {
      const nextBp = Math.max(0, Math.min(TOTAL_BP, percentToBp(percent)));
      return withUrlErrors(
        current.map((row) => (row.key === key ? { ...row, weightBp: nextBp } : row)),
      );
    });
  }, []);

  const toggleLock = useCallback((key: string) => {
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, locked: !row.locked } : row)),
    );
  }, []);

  const toggleEnabled = useCallback((key: string) => {
    // Stored weights are deliberately untouched: the 100% invariant is about
    // configuration, and selection renormalises over whatever is enabled.
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, enabled: !row.enabled } : row)),
    );
  }, []);

  const setUrl = useCallback((key: string, url: string) => {
    setRows((current) =>
      withUrlErrors(current.map((row) => (row.key === key ? { ...row, url } : row))),
    );
  }, []);

/* Prepended, and at zero.
   *
   * At zero because a new row that silently took a share off every existing one
   * would be the auto-rebalance problem again, in its most destructive form — a
   * live split changed by the act of adding a row.
   *
   * At the TOP because the row that was just added is the row about to be
   * filled in, and appending put it under however many destinations the entry
   * already had: on a thirty-way split, pressing Add scrolled the thing you
   * asked for off the bottom of the screen. The empty row now appears where
   * the eye already is, next to the button that made it.
   */
  const addRow = useCallback(() => {
    setRows((current) =>
      withUrlErrors([
        { key: nextKey(), id: null, url: '', weightBp: 0, enabled: true, locked: false },
        ...current,
      ]),
    );
  }, []);

  const addMany = useCallback((urls: readonly string[]) => {
    setRows((current) => {
      const existing = new Set(current.map((row) => row.url.trim()));
      const additions = urls
        .map((url) => url.trim())
        .filter((url) => url !== '' && !existing.has(url))
        .filter((url, index, all) => all.indexOf(url) === index)
        .map((url) => ({
          key: nextKey(),
          id: null,
          url,
          weightBp: 0,
          enabled: true,
          locked: false,
        }));

      if (additions.length === 0) return current;

      // At the top and at zero, like `addRow`, and for the same reasons:
      // importing a list must not rewrite the weights of the destinations
      // already carrying traffic, and what was just imported is what needs
      // looking at.
      return withUrlErrors([...additions, ...current]);
    });
  }, []);

  /* The remaining rows keep their weights, so removing a row leaves a visible
     gap rather than quietly inflating everything else. */
  const removeRow = useCallback((key: string) => {
    setRows((current) => {
      // An entry with no destinations cannot be saved, so refuse the last one.
      if (current.length <= 1) return current;
      return withUrlErrors(current.filter((row) => row.key !== key));
    });
  }, []);

  const distributeAll = useCallback(() => {
    setRows((current) => {
      /* Locks survive. They used to be cleared here on the theory that an
         explicit "distribute evenly" outranks a lock left over from an earlier
         adjustment — but a lock is the operator saying "this number is
         decided", and overriding it made the lock decorative. What is spread
         is whatever the locked rows left behind. */
      const next = distributeRemainder(
        current.map((row) => row.weightBp),
        lockedIndicesOf(current),
      );
      return withUrlErrors(
        current.map((row, index) => ({ ...row, weightBp: next[index] ?? row.weightBp })),
      );
    });
  }, []);

  const reset = useCallback(() => setRows(baseline), [baseline]);

  const effectivePct = useCallback(
    (key: string): number => {
      const row = rows.find((candidate) => candidate.key === key);
      if (row === undefined || !row.enabled || enabledTotalBp === 0) return 0;
      return Math.round((row.weightBp / enabledTotalBp) * 10_000) / 100;
    },
    [rows, enabledTotalBp],
  );

  const toPayload = useCallback(
    () =>
      rows.map((row) => ({ url: row.url.trim(), weightBp: row.weightBp, enabled: row.enabled })),
    [rows],
  );

  const weightsValid = validateWeightTotal(rows.map((row) => row.weightBp)).valid;
  const urlsValid = rows.every((row) => row.urlError === undefined);
  /* A destination at 0% is now perfectly saveable: it is parked, keeping its
     id and its traffic history, and the selector's cumulative walk can never
     land on a zero-width interval. It is still counted and still reported,
     because "this destination takes no traffic" is worth saying out loud —
     it just no longer blocks the save. */
  const unallocated = rows.filter((row) => row.weightBp === 0).length;

  /*
   * Dirty means the CONFIGURATION differs — not the row identities.
   *
   * Saving a split soft-deletes every destination and inserts the set again,
   * so every id changes on every save. Comparing ids therefore left the editor
   * permanently dirty the moment it was saved: the "Unsaved changes" line
   * stayed up, Reset stayed enabled, and the navigation guard went on warning
   * about a draft that had already been written. What the operator means by
   * unsaved is the URL, the weight, the enabled flag and the order — which is
   * exactly what is compared here.
   */
  const isDirty =
    rows.length !== baseline.length ||
    rows.some((row, index) => {
      const original = baseline[index];
      return (
        original === undefined ||
        original.url !== row.url.trim() ||
        original.weightBp !== row.weightBp ||
        original.enabled !== row.enabled
      );
    });

  return {
    rows,
    totalBp,
    remainingBp: TOTAL_BP - totalBp,
    isValid: weightsValid && urlsValid && rows.length > 0,
    unallocated,
    isDirty,
    hasDisabled: rows.some((row) => !row.enabled),
    effectivePct,
    setWeightPct,
    toggleLock,
    toggleEnabled,
    setUrl,
    addRow,
    addMany,
    removeRow,
    distributeAll,
    reset,
    toPayload,
  };
}
